<template>
  <div class="page-header-root">

    <!-- Sticky wrapper is transform-free, so stickiness isn't entangled with the
         horizontal full-bleed transform on the inner element. -->
    <div class="page-header--sticky" :style="stickyStyle">
      <div class="page-header--bleed" :style="bleedStyle">
        <div
          class="page-header page-header--bar d-flex align-center justify-space-between flex-wrap"
          :style="innerStyle"
        >
          <!-- Left: breadcrumb (the `title` slot overrides the default) -->
          <div class="d-flex align-center">
            <slot name="title">
              <div class="page-name d-flex align-center flex-wrap">
                <template v-for="(crumb, i) in visibleTrail">
                  <router-link
                    v-if="crumb.to && i < visibleTrail.length - 1"
                    :key="'crumb-' + i"
                    :to="crumb.to"
                    class="breadcrumb-crumb breadcrumb-crumb--link text-decoration-none"
                  >{{ crumb.text }}</router-link>
                  <span
                    v-else
                    :key="'crumb-' + i"
                    class="breadcrumb-crumb"
                  >{{ crumb.text }}</span>
                  <v-icon
                    v-if="i < visibleTrail.length - 1"
                    :key="'sep-' + i"
                    x-small
                    class="mx-2 breadcrumb-sep"
                  >fa-solid fa-chevron-right</v-icon>
                </template>
              </div>
            </slot>
          </div>

          <!-- Right: action slot + notifications + user menu -->
          <div class="d-flex align-center" style="gap: 16px;">
            <div v-if="$slots.right">
              <slot name="right" />
            </div>

            <alert-menu
              v-if="$vuetify.breakpoint.lgAndUp"
            />

            <user-menu />
          </div>
        </div>
      </div>
    </div>

    <slot />

  </div>

</template>

<script>
import AlertMenu from './AlertMenu/AlertMenu.vue'
import UserMenu from './UserMenu.vue'
import { routes } from '@/router'

export default {
  components: {
    AlertMenu,
    UserMenu
  },
  props: {
    page: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    description: {
      type: String,
      default: null
    },
    descriptionUrl: {
      type: String,
      default: null
    },
    breadcrumbs: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    // Width the persistent nav drawer reserves on lg+ (rail only on Home/collapsed
    // routes, rail + sub-nav elsewhere). Below lg the drawer is a temporary overlay
    // and reserves nothing, so v-main is the full viewport.
    drawerWidth () {
      if (!this.$vuetify.breakpoint.lgAndUp) return 0
      const collapsed = this.$route.path === '/' || this.$route.meta?.navCollapse
      return collapsed ? 90 : 330
    },
    sidePad () {
      const bp = this.$vuetify.breakpoint
      if (bp.xs) return 8
      if (bp.sm) return 16
      if (bp.md) return 24
      return 40
    },
    topPad () {
      const bp = this.$vuetify.breakpoint
      if (bp.sm) return 16
      return bp.md || bp.xs ? 24 : 40
    },
    // Stretch the bar across the whole v-main box. v-main__wrap centres its content,
    // so an element sized to the full main width and centred on the parent's centre
    // lines its edges up with the nav (left) and the viewport (right) — escaping the
    // 1400px content cap. The negative top margin pulls it up over the page padding.
    bleedStyle () {
      return {
        width: `calc(100vw - ${this.drawerWidth}px)`,
        marginLeft: '50%',
        transform: 'translateX(-50%)'
      }
    },
    // The sticky wrapper carries no transform (transforms on the sticky element itself
    // are the usual cause of "sticky not sticking"). The negative top margin pulls the
    // bar up over the page's top padding so it sits flush before it sticks.
    stickyStyle () {
      const style = {
        marginTop: `-${this.topPad}px`
      }
      // Stick to the top on lg+ (on smaller screens there's a separate mobile top bar).
      if (this.$vuetify.breakpoint.lgAndUp) {
        style.position = 'sticky'
        style.top = '0'
      }
      return style
    },
    // Re-cap the bar contents at the same 1400px + padding as the page body so the
    // pill/title line up with the content below even though the bar itself is full width.
    innerStyle () {
      return {
        paddingLeft: `${this.sidePad}px`,
        paddingRight: `${this.sidePad}px`
      }
    },
    // Users without home-dashboard access are redirected off '/' (see HomeView),
    // so don't offer them a Dashboard crumb that leads somewhere they can't go.
    hasDashboardAccess () {
      return this.$hasRole('SUPERUSER') ||
        (this.$hasRole('TECHNICAL') && this.$hasRole('CONTENT'))
    },
    // Breadcrumb: Dashboard > Section > Page, derived from the current route.
    // The Dashboard crumb (and its divider) only render with dashboard access.
    breadcrumbTrail () {
      const trail = this.hasDashboardAccess ? [{ text: 'Dashboard', to: '/' }] : []
      const meta = this.$route.meta || {}
      const nav = meta.nav
      if (!nav) return trail

      const sectionPath = meta.breadcrumb_path || ('/' + nav)
      const landing = routes.find(route => route.path === sectionPath)
      const sectionName = landing ? (landing.meta?.name || landing.name) : (meta.breadcrumb_name || nav)
      const pageName = this.page || meta.name || this.$route.name

      if (this.$route.path === sectionPath) {
        // On the section landing itself — no third crumb.
        trail.push({ text: sectionName, to: null })
      } else {
        trail.push({ text: sectionName, to: sectionPath })
        trail.push({ text: pageName, to: null })
      }
      return trail
    },
    // On mobile show only one level deep (the last two crumbs).
    visibleTrail () {
      if (this.$vuetify.breakpoint.smAndDown && this.breadcrumbTrail.length > 2) {
        return this.breadcrumbTrail.slice(-2)
      }
      return this.breadcrumbTrail
    }
  }
}
</script>

<style lang="scss">
/* Root generates no box (display:contents), so the sticky wrapper's containing
   block becomes the tall page root rather than this short header-only wrapper —
   that's what gives sticky room to travel. */
.page-header-root {
  display: contents;
}
.page-header--sticky {
  z-index: 4;
}
.page-header--bleed {
  box-sizing: border-box;
  background-color: #ffffff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
}
.page-header {
  &.page-header--bar {
    box-sizing: border-box;
    max-width: 1400px;
    margin: 0 auto;
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .page-name {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.2;
  }
  .breadcrumb-crumb--link {
    color: inherit;
    opacity: 0.55;
    &:hover {
      opacity: 1;
    }
  }
  .breadcrumb-sep {
    opacity: 0.4;
  }
  .header-divider {
    width: 1px;
    height: 24px;
    background-color: rgba(0, 0, 0, 0.14);
    flex: 0 0 auto;
  }
  // Shrink icon/kebab buttons that pages inject into the header's action slot.
  .v-btn--icon.v-size--default {
    height: 32px;
    width: 32px;
  }
}
.theme--dark .page-header--bleed {
  background-color: #1e1e1e;
  border-bottom-color: rgba(255, 255, 255, 0.09);
}
.theme--dark .page-header .header-divider {
  background-color: rgba(255, 255, 255, 0.16);
}
</style>
